import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { GoalFormData, SmartCriteria } from '@/types/goals';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';
// goalsService import removed - not needed in form component
import { Plus, X, Sparkles, Loader2, Send } from 'lucide-react';

const smartCriteriaSchema = z.object({
  specific: z.string().min(10, 'Please be more specific (at least 10 characters)'),
  measurable: z.string().min(10, 'Please describe how you will measure this (at least 10 characters)'),
  achievable: z.string().min(10, 'Please describe how you will achieve this (at least 10 characters)'),
  relevant: z.string().min(10, 'Please explain why this goal matters (at least 10 characters)'),
  timeBound: z.string().min(5, 'Please specify when you will complete this goal'),
});

const goalSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters').max(500, 'Description must be less than 500 characters'),
  smartCriteria: smartCriteriaSchema,
  deadline: z.string().optional(),
  startDate: z.string().optional(),
  aspirationId: z.string().min(1, 'Please select an aspiration for this goal'),
});

type GoalFormInputs = z.infer<typeof goalSchema>;

interface GoalFormProps {
  isOpen: boolean;
  onClose: () => void;
  goalId?: string;
  isEditing?: boolean;
  selectedAspirationId?: string;
}

// Categories removed - goals are organized by aspirations

const smartLabels = {
  specific: 'Specific - What exactly will you accomplish?',
  measurable: 'Measurable - How will you measure progress and success?',
  achievable: 'Achievable - How will you accomplish this goal?',
  relevant: 'Relevant - Why does this goal matter to you?',
  timeBound: 'Time-bound - When will you complete this goal?',
};

export function GoalForm({ 
  isOpen, 
  onClose, 
  goalId,
  isEditing = false,
  selectedAspirationId
}: GoalFormProps) {
  const { getGoalById, createGoal, updateGoal, goals } = useGoalsStore();
  const { aspirations, initializeAspirations } = useAspirationsStore();
  
  const [successMetrics, setSuccessMetrics] = useState<string[]>(['']);
  const [resources, setResources] = useState<string[]>(['']);

  // AI assistant state
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiSection, setShowAiSection] = useState(false);
  
  const existingGoal = goalId ? getGoalById(goalId) : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
    getValues
  } = useForm<GoalFormInputs>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      title: '',
      description: '',
      smartCriteria: {
        specific: '',
        measurable: '',
        achievable: '',
        relevant: '',
        timeBound: '',
      },
      deadline: '',
      startDate: '',
      aspirationId: selectedAspirationId || '',
    }
  });

  // Update form values when goal data changes
  useEffect(() => {
    if (existingGoal && isOpen && aspirations.length > 0) {
      setValue('title', existingGoal.title);
      setValue('description', existingGoal.description);
      // Category removed
      setValue('smartCriteria', existingGoal.smartCriteria);
      setValue('deadline', existingGoal.deadline ? 
        existingGoal.deadline.toISOString().split('T')[0] : '');
      setValue('startDate', existingGoal.startDate ? 
        existingGoal.startDate.toISOString().split('T')[0] : '');
      
      // Ensure the aspirationId exists in the aspirations list before setting it
      const aspirationExists = aspirations.some(asp => asp.id === existingGoal.aspirationId);
      if (aspirationExists) {
        setValue('aspirationId', existingGoal.aspirationId);
      } else {
        console.warn(`⚠️ Aspiration ID "${existingGoal.aspirationId}" not found in aspirations list`);
        setValue('aspirationId', '');
      }
      
      setSuccessMetrics(existingGoal.successMetrics || ['']);
      setResources(existingGoal.resources || ['']);
    } else if (!isEditing && isOpen) {
      // Reset to default values for new goals
      reset({
        title: '',
        description: '',
        smartCriteria: {
          specific: '',
          measurable: '',
          achievable: '',
          relevant: '',
          timeBound: '',
        },
        deadline: '',
        startDate: '',
        aspirationId: selectedAspirationId || '',
      });
      setSuccessMetrics(['']);
      setResources(['']);
    }
  }, [existingGoal, isOpen, isEditing, selectedAspirationId, aspirations, setValue, reset]);

  // Watch form values for AI context
  const watchedValues = watch();

  // Initialize aspirations if empty
  useEffect(() => {
    if (aspirations.length === 0) {
      console.log('🔄 Initializing aspirations from GoalForm');
      initializeAspirations();
    }
  }, [aspirations.length, initializeAspirations]);

  // Debug effect to track form state
  useEffect(() => {
    if (isOpen && existingGoal) {
      console.log('🔍 GoalForm Debug Info:');
      console.log('  Existing Goal:', {
        id: existingGoal.id,
        title: existingGoal.title,
        aspirationId: existingGoal.aspirationId
      });
      console.log('  Aspirations loaded:', aspirations.length);
      console.log('  Available aspiration IDs:', aspirations.map(a => a.id));
    }
  }, [isOpen, existingGoal, aspirations]);

  // AI handler function
  const handleAskAI = async () => {
    const selectedAspirationId = watchedValues.aspirationId;
    if (!selectedAspirationId) {
      setAiError('Please select an aspiration first');
      return;
    }

    // If no user input, generate a goal suggestion based on the aspiration alone
    const userInputText = aiInput.trim() || 'Suggest a SMART goal that advances this aspiration';

    const selectedAspiration = aspirations.find(a => a.id === selectedAspirationId);
    if (!selectedAspiration) {
      setAiError('Selected aspiration not found');
      return;
    }

    if (!aiService.isConfigured()) {
      setAiError('AI service not configured. Please add your API key in Settings.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);
    
    try {
      const profile = personaService.getProfile();
      
      // Get current form values to provide as context
      const currentValues = getValues();
      
      const suggestion = await aiService.generateGoalSuggestion({
        userInput: userInputText,
        linkedAspiration: {
          id: selectedAspiration.id,
          title: selectedAspiration.title,
          description: selectedAspiration.description,
          category: selectedAspiration.category
        },
        existingGoals: goals.map(g => ({
          title: g.title,
          description: g.description,
          aspirationId: g.aspirationId
        })),
        userProfile: {
          name: profile.name,
          jobRole: profile.jobRole,
          age: profile.age
        },
        currentFormData: {
          title: currentValues.title,
          description: currentValues.description,
          smartCriteria: currentValues.smartCriteria,
          successMetrics: successMetrics.filter(m => m.trim()),
          resources: resources.filter(r => r.trim())
        }
      });
      
      // Populate form fields with AI suggestion
      setValue('title', suggestion.title);
      setValue('description', suggestion.description);
      setValue('smartCriteria', suggestion.smartCriteria);
      
      // Update success metrics and resources if provided
      if (suggestion.suggestedMetrics && suggestion.suggestedMetrics.length > 0) {
        setSuccessMetrics([...suggestion.suggestedMetrics, '']);
      }
      if (suggestion.suggestedResources && suggestion.suggestedResources.length > 0) {
        setResources([...suggestion.suggestedResources, '']);
      }
      
      // Clear AI input after successful generation
      setAiInput('');
      setShowAiSection(false);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to generate suggestion');
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleFormSubmit = async (data: GoalFormInputs) => {
    try {
      const formData: GoalFormData = {
        ...data,
        priority: 1, // New goals always get top priority
        deadline: data.deadline ? new Date(data.deadline) : undefined,
        startDate: data.startDate ? new Date(data.startDate) : undefined,
        successMetrics: successMetrics.filter(metric => metric.trim()),
        resources: resources.filter(resource => resource.trim()),
      };

      if (isEditing && goalId) {
        await updateGoal(goalId, formData);
      } else {
        await createGoal(formData);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSuccessMetrics(['']);
    setResources(['']);
    setAiInput('');
    setAiError(null);
    setShowAiSection(false);
    onClose();
  };

  const addMetric = () => setSuccessMetrics([...successMetrics, '']);
  const removeMetric = (index: number) => setSuccessMetrics(prev => prev.filter((_, i) => i !== index));
  const updateMetric = (index: number, value: string) => {
    setSuccessMetrics(prev => prev.map((metric, i) => i === index ? value : metric));
  };

  const addResource = () => setResources([...resources, '']);
  const removeResource = (index: number) => setResources(prev => prev.filter((_, i) => i !== index));
  const updateResource = (index: number, value: string) => {
    setResources(prev => prev.map((resource, i) => i === index ? value : resource));
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={isEditing ? 'Edit Goal' : 'Create New Goal'}
      description="Build a SMART goal that connects to your aspirations"
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Title *</label>
          <Input
            data-testid="goal-title-input"
            {...register('title')}
            placeholder="What do you want to achieve?"
            error={errors.title?.message}
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Description *</label>
          <textarea
            {...register('description')}
            placeholder="Describe your goal in detail..."
            className="w-full min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
          {errors.description && (
            <p className="text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Aspiration Link */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Link to Aspiration *</label>
          <NativeSelect
            data-testid="goal-aspiration-select"
            {...register('aspirationId')}
            error={errors.aspirationId?.message}
          >
            <option value="">Select an aspiration...</option>
            {aspirations.map(aspiration => (
              <option key={aspiration.id} value={aspiration.id}>
                {aspiration.title}
              </option>
            ))}
          </NativeSelect>
          {errors.aspirationId && (
            <p className="text-sm text-red-600">{errors.aspirationId.message}</p>
          )}
        </div>

        {/* SMART Criteria Section */}
        <div className="space-y-4 border-t pt-6">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">SMART Goal Framework</h3>
            <Badge variant="outline">Required</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Define your goal using the SMART framework to increase your chances of success.
          </p>

          {Object.entries(smartLabels).map(([key, label]) => (
            <div key={key} className="space-y-2">
              <label className="text-sm font-medium">{label} *</label>
              <textarea
                {...register(`smartCriteria.${key as keyof SmartCriteria}`)}
                placeholder={`Enter ${key} criteria...`}
                className="w-full min-h-[60px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
              {errors.smartCriteria?.[key as keyof SmartCriteria] && (
                <p className="text-sm text-red-600">
                  {errors.smartCriteria[key as keyof SmartCriteria]?.message}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Start Date</label>
            <Input
              {...register('startDate')}
              type="date"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Deadline</label>
            <Input
              {...register('deadline')}
              type="date"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        {/* Success Metrics */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Success Metrics</label>
            <Button type="button" variant="outline" size="sm" onClick={addMetric}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {successMetrics.map((metric, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={metric}
                onChange={(e) => updateMetric(index, e.target.value)}
                placeholder="How will you measure success?"
                className="flex-1"
              />
              {successMetrics.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeMetric(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Resources */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Resources Needed</label>
            <Button type="button" variant="outline" size="sm" onClick={addResource}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {resources.map((resource, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={resource}
                onChange={(e) => updateResource(index, e.target.value)}
                placeholder="What resources do you need?"
                className="flex-1"
              />
              {resources.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeResource(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* AI Assistant Section */}
        {!isEditing && aiService.isConfigured() && (
          <div className="space-y-2 border-t pt-4">
            {!showAiSection ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAiSection(true)}
                className="w-full gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Ask AI for Help
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Ask AI for SMART Goal Help
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAiSection(false);
                      setAiInput('');
                      setAiError(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Close
                  </button>
                </div>
                
                <div className="flex gap-2">
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder={watchedValues.aspirationId 
                      ? "Optional: Describe your specific goal idea, or leave blank to let AI suggest one based on your aspiration... (e.g., 'Complete a marathon' or 'improve my fitness level')"
                      : "Please select an aspiration first..."}
                    className="flex-1 min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    disabled={isAiLoading || !watchedValues.aspirationId}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        handleAskAI();
                      }
                    }}
                  />
                </div>
                
                {aiError && (
                  <Alert variant="destructive" className="text-sm">
                    {aiError}
                  </Alert>
                )}
                
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {watchedValues.aspirationId ? (
                      <>AI will create a SMART goal for: <strong>{aspirations.find(a => a.id === watchedValues.aspirationId)?.title}</strong></>
                    ) : (
                      'Select an aspiration above to enable AI assistance'
                    )}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAskAI}
                    disabled={isAiLoading || !watchedValues.aspirationId}
                    className="gap-2"
                  >
                    {isAiLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Send className="h-3 w-3" />
                        Generate SMART Goal
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

      </form>
      
      {/* Form Actions - Fixed at bottom */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4 bg-background sticky bottom-0">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Update Goal' : 'Create Goal'}
        </Button>
      </div>
    </Modal>
  );
}